/**
 * EditListingModal.tsx
 *
 * Governed Catalog Listing Editor with Live Split-View Preview,
 * Field Immutability Locks, Draft Recovery, and 3-Way OCC Conflict Resolution.
 */

import React, { useState, useEffect } from 'react';
import { globalCatalogService } from '../../plugins/commerce/catalog-service.js';
import { globalInventoryMovementLedger } from '../../plugins/commerce/inventory/inventoryMovementLedger.js';
import { validateListingPatch, type ListingPatchValidationResult } from '../../plugins/commerce/governance/listing-governance.js';
import {
  computeThreeWayMerge,
  saveEphemeralDraft,
  loadEphemeralDraft,
  clearEphemeralDraft,
  type ListingDraft,
} from '../../plugins/commerce/governance/listing-merge-service.js';
import type { ChannelListingOverride } from '../../plugins/commerce/governance/channel-override-contracts.js';
import { ListingPreviewPane } from './ListingPreviewPane.js';
import { ChannelOverridesTable } from './ChannelOverridesTable.js';
import { ListingDiffReviewModal } from './ListingDiffReviewModal.js';
import { MediaDropzone } from './MediaDropzone.js';
import type { Product, ProductVariant, CommerceScope, ProductStatus } from '../../plugins/commerce/commerce-contracts.js';
import type { MediaAsset } from '../../plugins/commerce/inventory/inventoryContracts.js';
import type { AuthorizationContext } from '../../kernel/authorization/capabilities.js';

export interface EditListingModalProps {
  readonly isOpen: boolean;
  readonly productId: string | null;
  readonly authContext: AuthorizationContext;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}

type TabType = 'details' | 'overrides' | 'media';

export const EditListingModal: React.FC<EditListingModalProps> = ({
  isOpen,
  productId,
  authContext,
  onClose,
  onSaved,
}) => {
  if (!isOpen || !productId) return null;

  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [originalProduct, setOriginalProduct] = useState<Product | null>(null);
  const [originalVariants, setOriginalVariants] = useState<readonly ProductVariant[]>([]);

  // Editable Form State
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<ProductStatus>('active');
  const [basePriceFormatted, setBasePriceFormatted] = useState('0.00');
  const [currency, setCurrency] = useState('USD');
  const [category, setCategory] = useState('Apparel');
  const [attachedMedia, setAttachedMedia] = useState<readonly MediaAsset[]>([]);
  const [channelOverrides, setChannelOverrides] = useState<readonly ChannelListingOverride[]>([]);
  const [autoSaveTime, setAutoSaveTime] = useState<string | null>(null);

  // Draft Recovery State
  const [hasRecoverableDraft, setHasRecoverableDraft] = useState(false);
  const [recoveredDraft, setRecoveredDraft] = useState<ListingDraft | null>(null);

  // Review & Concurrency State
  const [isDiffReviewOpen, setIsDiffReviewOpen] = useState(false);
  const [validationResult, setValidationResult] = useState<ListingPatchValidationResult | null>(null);
  const [concurrencyConflictBanner, setConcurrencyConflictBanner] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scope: CommerceScope = {
    meshId: 'mesh-alpha',
    workspaceId: 'ws-main',
    salesChannelId: 'channel-online',
    actorPrincipalId: authContext.principal.id,
    policyVersion: '1.0.0',
  };

  // 1. Initial Load & Hydration
  useEffect(() => {
    const prod = globalCatalogService.getProduct(scope, productId);
    if (prod) {
      setOriginalProduct(prod);
      const vars = globalCatalogService.getVariantsForProduct(productId);
      setOriginalVariants(vars);

      const titleStr = typeof prod.title === 'string' ? prod.title : Object.values(prod.title)[0] || '';
      const descStr = typeof prod.description === 'string' ? prod.description : prod.description ? Object.values(prod.description)[0] || '' : '';

      setTitle(titleStr);
      setDescription(descStr);
      setStatus(prod.status);
      setCategory(prod.categoryIds?.[0] || 'Apparel');

      const primaryVar = vars[0];
      if (primaryVar) {
        const cents = parseInt(primaryVar.basePriceMinor || '0', 10);
        setBasePriceFormatted((cents / 100).toFixed(2));
        setCurrency(primaryVar.currency || 'USD');
      }

      // Hydrate existing media
      if (prod.media && prod.media.length > 0) {
        const hydratedMedia: MediaAsset[] = prod.media.map((m, idx) => ({
          mediaId: m.attachmentId,
          ownerType: 'product',
          ownerId: productId,
          role: idx === 0 ? 'primary' : 'gallery',
          mimeType: (m.mimeType as any) || 'image/webp',
          contentHash: m.contentHash,
          byteLength: m.sizeBytes || 1024,
          altText: (m.altText as any) || {},
          altTextStatus: 'provided',
          visibility: 'public-catalog',
          sortOrder: idx,
          storageRef: `blob://${m.contentHash}`,
          revision: 1,
          createdAt: new Date().toISOString(),
        }));
        setAttachedMedia(hydratedMedia);
      }

      // Check for uncommitted draft in localStorage
      if (typeof window !== 'undefined' && window.localStorage) {
        const draft = loadEphemeralDraft(window.localStorage, 'ws-main', authContext.principal.id, productId);
        if (draft && draft.baseRevision === prod.revision) {
          setHasRecoverableDraft(true);
          setRecoveredDraft(draft);
        }
      }
    }
  }, [productId]);

  // 2. Debounced Auto-Save Draft
  useEffect(() => {
    if (!originalProduct || typeof window === 'undefined' || !window.localStorage) return;

    const timer = setTimeout(() => {
      const draftObj: ListingDraft = {
        draftId: `draft_${productId}_${Date.now()}`,
        productId,
        workspaceId: 'ws-main',
        authorId: authContext.principal.id,
        deviceId: 'device-local',
        editorSessionId: authContext.principal.sessionId || 'session-1',
        baseRevision: originalProduct.revision,
        contentHash: 'hash-draft',
        draftSavedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        content: {
          title: { 'en-US': title },
          description: { 'en-US': description },
          status,
        },
      };
      saveEphemeralDraft(window.localStorage, draftObj);
      setAutoSaveTime(new Date().toLocaleTimeString());
    }, 400);

    return () => clearTimeout(timer);
  }, [title, description, status, basePriceFormatted, attachedMedia, productId, originalProduct]);

  const handleRestoreDraft = () => {
    if (!recoveredDraft) return;
    if (recoveredDraft.content.title) {
      setTitle(Object.values(recoveredDraft.content.title)[0] || '');
    }
    if (recoveredDraft.content.description) {
      setDescription(Object.values(recoveredDraft.content.description)[0] || '');
    }
    if (recoveredDraft.content.status) {
      setStatus(recoveredDraft.content.status);
    }
    setHasRecoverableDraft(false);
  };

  const handleDiscardDraft = () => {
    if (typeof window !== 'undefined' && window.localStorage) {
      clearEphemeralDraft(window.localStorage, 'ws-main', authContext.principal.id, productId);
    }
    setHasRecoverableDraft(false);
    setRecoveredDraft(null);
  };

  // Convert attachedMedia to AttachmentRefs
  const mediaRefs = attachedMedia.map((m) => ({
    attachmentId: m.mediaId,
    mimeType: m.mimeType,
    contentHash: m.contentHash,
    sizeBytes: m.byteLength,
    altText: m.altText as any,
  }));

  // Draft representation for live preview
  const draftProduct: Partial<Product> = {
    ...originalProduct,
    title: { 'en-US': title },
    description: description ? { 'en-US': description } : undefined,
    status,
    categoryIds: [category],
    media: mediaRefs,
  };

  const priceCents = Math.round(parseFloat(basePriceFormatted || '0') * 100);
  const draftVariants: readonly Partial<ProductVariant>[] = originalVariants.map((v) => ({
    ...v,
    basePriceMinor: String(priceCents),
    currency,
  }));

  const handleReviewClick = () => {
    if (!originalProduct) return;

    const variantPatches = originalVariants.map((v) => ({
      variantId: v.variantId,
      patch: { basePriceMinor: String(priceCents) },
    }));

    const val = validateListingPatch(originalProduct, draftProduct, originalVariants, variantPatches);
    setValidationResult(val);
    setIsDiffReviewOpen(true);
  };

  const handleDirectSave = async () => {
    if (!originalProduct) return;

    const variantPatches = originalVariants.map((v) => ({
      variantId: v.variantId,
      patch: { basePriceMinor: String(priceCents) },
    }));

    const val = validateListingPatch(originalProduct, draftProduct, originalVariants, variantPatches);
    if (!val.allowed) {
      setValidationResult(val);
      setIsDiffReviewOpen(true);
      return;
    }

    if (val.requiresSupervisorMfa) {
      setValidationResult(val);
      setIsDiffReviewOpen(true);
      return;
    }

    setIsSubmitting(true);
    setConcurrencyConflictBanner(null);

    try {
      await globalCatalogService.saveListing(scope, {
        productId,
        expectedProductRevision: originalProduct.revision,
        productPatch: {
          title: draftProduct.title,
          description: draftProduct.description,
          status: draftProduct.status,
          categoryIds: draftProduct.categoryIds,
          media: mediaRefs,
        },
        variantPatches: originalVariants.map((v) => ({
          variantId: v.variantId,
          expectedRevision: v.revision,
          patch: {
            basePriceMinor: String(priceCents),
            currency,
          },
        })),
        idempotencyKey: `save_${productId}_${Date.now()}`,
      });

      // Update inventory movement ledger product names for immediate ledger sync
      const titleStr = typeof draftProduct.title === 'string'
        ? draftProduct.title
        : draftProduct.title?.['en-US'] || Object.values(draftProduct.title || {})[0] || 'Product';
      originalVariants.forEach((v) => {
        globalInventoryMovementLedger.setProductName(v.sku, titleStr);
      });

      if (typeof window !== 'undefined' && window.localStorage) {
        clearEphemeralDraft(window.localStorage, 'ws-main', authContext.principal.id, productId);
      }

      onSaved();
      onClose();
    } catch (err: any) {
      setConcurrencyConflictBanner(err.message || 'Failed to save listing');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCommitPublish = async () => {
    if (!originalProduct || !validationResult || !validationResult.allowed) return;

    setIsSubmitting(true);
    setConcurrencyConflictBanner(null);

    try {
      const liveProduct = globalCatalogService.getProduct(scope, productId);
      if (!liveProduct) throw new Error('Product was deleted remotely.');

      let effectiveTitle = draftProduct.title;
      let effectiveDescription = draftProduct.description;
      let effectiveStatus = draftProduct.status;
      let effectiveCategories = draftProduct.categoryIds;

      if (liveProduct.revision !== originalProduct.revision) {
        const mergeResult = computeThreeWayMerge(originalProduct, draftProduct, liveProduct);

        if (mergeResult.status === 'conflict') {
          const conflictNames = mergeResult.conflicts.map((c) => c.fieldPath).join(', ');
          setConcurrencyConflictBanner(`Revision conflict on revision #${liveProduct.revision}. Conflicting fields: ${conflictNames}.`);
          setIsDiffReviewOpen(false);
          setIsSubmitting(false);
          return;
        }

        effectiveTitle = mergeResult.merged?.title;
        effectiveDescription = mergeResult.merged?.description;
        effectiveStatus = mergeResult.merged?.status;
        effectiveCategories = mergeResult.merged?.categoryIds;
      }

      await globalCatalogService.saveListing(scope, {
        productId,
        expectedProductRevision: liveProduct.revision,
        productPatch: {
          title: effectiveTitle,
          description: effectiveDescription,
          status: effectiveStatus,
          categoryIds: effectiveCategories,
          media: mediaRefs,
        },
        variantPatches: originalVariants.map((v) => ({
          variantId: v.variantId,
          patch: {
            basePriceMinor: String(priceCents),
            currency,
          },
        })),
        idempotencyKey: `save_commit_${productId}_${Date.now()}`,
      });

      // Update inventory movement ledger product names for immediate ledger sync
      const titleStr = typeof effectiveTitle === 'string'
        ? effectiveTitle
        : effectiveTitle?.['en-US'] || Object.values(effectiveTitle || {})[0] || 'Product';
      originalVariants.forEach((v) => {
        globalInventoryMovementLedger.setProductName(v.sku, titleStr);
      });

      if (typeof window !== 'undefined' && window.localStorage) {
        clearEphemeralDraft(window.localStorage, 'ws-main', authContext.principal.id, productId);
      }

      setIsDiffReviewOpen(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setConcurrencyConflictBanner(err.message || 'Failed to update product');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(8px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        style={{
          backgroundColor: '#111827',
          border: '1px solid var(--theme-border, #1e293b)',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '1100px',
          height: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 60px rgba(0,0,0,0.8)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid #1e293b',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#0f172a',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '18px' }}>✏️</span>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h2 style={{ fontSize: '16px', fontWeight: 800, margin: 0, color: '#f8fafc' }}>
                  Governed Listing Editor & Live Simulator
                </h2>
                {autoSaveTime && (
                  <span style={{ fontSize: '10px', color: '#34d399', backgroundColor: 'rgba(52, 211, 153, 0.15)', padding: '2px 6px', borderRadius: '4px', fontWeight: 700 }}>
                    ● Auto-saved {autoSaveTime}
                  </span>
                )}
              </div>
              <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                SKU: <code style={{ color: '#38bdf8' }}>{originalVariants[0]?.sku || 'N/A'}</code> | Revision: #{originalProduct?.revision || 1}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '20px', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>

        {/* Recoverable Draft Alert Banner */}
        {hasRecoverableDraft && (
          <div
            style={{
              padding: '8px 20px',
              backgroundColor: 'rgba(56, 189, 248, 0.15)',
              borderBottom: '1px solid #38bdf8',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '12px',
              color: '#bae6fd',
            }}
          >
            <span>⚡ You have an unsaved local draft for this product revision.</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={handleRestoreDraft}
                style={{ padding: '4px 10px', borderRadius: '4px', backgroundColor: '#38bdf8', color: '#000', fontWeight: 800, fontSize: '11px', border: 'none', cursor: 'pointer' }}
              >
                Restore Draft
              </button>
              <button
                type="button"
                onClick={handleDiscardDraft}
                style={{ padding: '4px 10px', borderRadius: '4px', backgroundColor: 'transparent', color: '#94a3b8', border: '1px solid #334155', fontSize: '11px', cursor: 'pointer' }}
              >
                Discard
              </button>
            </div>
          </div>
        )}

        {/* Concurrency Conflict Error Banner */}
        {concurrencyConflictBanner && (
          <div
            style={{
              padding: '10px 20px',
              backgroundColor: 'rgba(239, 68, 68, 0.15)',
              borderBottom: '1px solid #ef4444',
              color: '#fca5a5',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            ⚠️ {concurrencyConflictBanner}
          </div>
        )}

        {/* Main Split-Screen Workspace */}
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1.2fr 1fr', overflow: 'hidden' }}>
          {/* LEFT: Governed Editor Form */}
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto', borderRight: '1px solid #1e293b' }}>
            {/* Tab Header */}
            <div style={{ display: 'flex', borderBottom: '1px solid #1e293b', backgroundColor: 'rgba(0,0,0,0.2)' }}>
              {[
                { id: 'details', label: '1. Details & Status' },
                { id: 'overrides', label: '2. Channel Overrides' },
                { id: 'media', label: '3. Media & Alt-Text' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id as TabType)}
                  style={{
                    padding: '10px 16px',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: activeTab === tab.id ? '#38bdf8' : '#94a3b8',
                    borderBottom: activeTab === tab.id ? '2px solid #38bdf8' : '2px solid transparent',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* TAB 1: DETAILS */}
              {activeTab === 'details' && (
                <>
                  {/* Immutable Fields Notice Box */}
                  <div
                    style={{
                      padding: '10px 14px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255,255,255,0.02)',
                      border: '1px solid #1e293b',
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: '8px',
                    }}
                  >
                    <div>
                      <span style={{ fontSize: '10px', color: '#94a3b8', display: 'block' }}>🔒 Product ID</span>
                      <code style={{ fontSize: '11px', color: '#cbd5e1' }}>{originalProduct?.productId.slice(0, 14)}...</code>
                    </div>
                    <div>
                      <span style={{ fontSize: '10px', color: '#94a3b8', display: 'block' }}>🔒 Master SKU</span>
                      <code style={{ fontSize: '11px', color: '#cbd5e1' }}>{originalVariants[0]?.sku || 'N/A'}</code>
                    </div>
                    <div>
                      <span style={{ fontSize: '10px', color: '#94a3b8', display: 'block' }}>🔒 Sector Schema</span>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#38bdf8', textTransform: 'capitalize' }}>
                        {originalProduct?.schemaExtension?.sector || 'Physical'}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>
                      Product Title
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        backgroundColor: '#1e293b',
                        color: '#fff',
                        border: '1px solid #334155',
                        fontSize: '13px',
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '4px' }}>
                      Description
                    </label>
                    <textarea
                      rows={3}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        backgroundColor: '#1e293b',
                        color: '#fff',
                        border: '1px solid #334155',
                        fontSize: '13px',
                      }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>
                        Base Price ({currency})
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={basePriceFormatted}
                        onChange={(e) => setBasePriceFormatted(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '8px',
                          backgroundColor: '#1e293b',
                          color: '#fff',
                          border: '1px solid #334155',
                          fontSize: '13px',
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>
                        Category
                      </label>
                      <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '8px',
                          backgroundColor: '#1e293b',
                          color: '#fff',
                          border: '1px solid #334155',
                          fontSize: '12px',
                        }}
                      >
                        <option value="AISurveillance">AI Surveillance &amp; Cameras</option>
                        <option value="BiometricAccess">Biometric Access &amp; Smart Locks</option>
                        <option value="DataSecurity">Data Security &amp; Hardware MFA</option>
                        <option value="RoboticsPatrol">Autonomous Robotics &amp; Patrol</option>
                        <option value="BallisticProtection">Ballistic Armor &amp; Shields</option>
                        <option value="ExecutiveProtection">Executive Protection &amp; Counter-Surveillance</option>
                        <option value="EliteFieldTech">Elite Tactical &amp; Night Vision Tech</option>
                        <option value="Electronics">General Electronics</option>
                        <option value="Apparel">Apparel &amp; Gear</option>
                        <option value="Services">Security Services</option>
                        <option value="DigitalGoods">Digital Security Keys</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px' }}>
                        Listing Status
                      </label>
                      <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value as ProductStatus)}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          borderRadius: '8px',
                          backgroundColor: '#1e293b',
                          color: '#fff',
                          border: '1px solid #334155',
                          fontSize: '12px',
                        }}
                      >
                        <option value="active">Active (Visible)</option>
                        <option value="review">Under Review</option>
                        <option value="draft">Draft</option>
                        <option value="archived">Archived (Delisted)</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

              {/* TAB 2: CHANNEL OVERRIDES */}
              {activeTab === 'overrides' && (
                <ChannelOverridesTable
                  productId={productId}
                  basePriceFormatted={basePriceFormatted}
                  currency={currency}
                  overrides={channelOverrides}
                  onChange={setChannelOverrides}
                />
              )}

              {/* TAB 3: MEDIA */}
              {activeTab === 'media' && (
                <MediaDropzone
                  ownerType="product"
                  ownerId={productId}
                  existingMedia={attachedMedia}
                  authContext={authContext}
                  onMediaAttached={(m) => setAttachedMedia((prev) => [...prev, m])}
                  onMediaRemoved={(id) => setAttachedMedia((prev) => prev.filter((m) => m.mediaId !== id))}
                />
              )}
            </div>
          </div>

          {/* RIGHT: Live Preview Simulator */}
          <div style={{ height: '100%', overflow: 'hidden' }}>
            <ListingPreviewPane
              draftProduct={draftProduct}
              draftVariants={draftVariants}
              draftMedia={attachedMedia}
              authContext={authContext}
            />
          </div>
        </div>

        {/* Footer Actions */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #1e293b',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#0f172a',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{ padding: '8px 16px', borderRadius: '6px', backgroundColor: 'transparent', border: '1px solid #334155', color: '#94a3b8', cursor: 'pointer' }}
          >
            Cancel
          </button>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={handleReviewClick}
              style={{
                padding: '9px 16px',
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                color: '#cbd5e1',
                fontWeight: 700,
                fontSize: '12px',
                border: '1px solid #334155',
                cursor: 'pointer',
              }}
            >
              🔍 Review Diff
            </button>
            <button
              type="button"
              onClick={handleDirectSave}
              disabled={isSubmitting}
              style={{
                padding: '9px 22px',
                borderRadius: '8px',
                backgroundColor: '#38bdf8',
                color: '#000',
                fontWeight: 800,
                fontSize: '13px',
                border: 'none',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 14px rgba(56, 189, 248, 0.3)',
              }}
            >
              {isSubmitting ? 'Saving...' : '💾 Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Pre-Commit Diff & AAL3 Confirmation Modal */}
      {originalProduct && validationResult && (
        <ListingDiffReviewModal
          isOpen={isDiffReviewOpen}
          originalProduct={originalProduct}
          proposedProduct={draftProduct}
          originalVariants={originalVariants}
          proposedVariants={originalVariants.map((v) => ({
            variantId: v.variantId,
            patch: { basePriceMinor: String(priceCents) },
          }))}
          validationResult={validationResult}
          isSubmitting={isSubmitting}
          onConfirm={handleCommitPublish}
          onCancel={() => setIsDiffReviewOpen(false)}
        />
      )}
    </div>
  );
};
